import styled from '@emotion/styled'
import React from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../../i18n/localeUtils'
import { Link, useLoaderData } from 'react-router'

import { Grid, LinearProgress, Divider as MuiDivider, Tooltip, Typography } from '@mui/material'

import { spacing } from '@mui/system'
import { flagPath } from '../../../components/entities/CompaniesGrid'
import { normalizedName } from '../../../components/entities/CompanyName'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import CompanyDetailsView from './CompanyDetailsView'
import { WatchListButton } from '../../components/WatchListButton'
import useAuth from '../../../hooks/useAuth'
import Button from '@mui/material/Button'

const Divider = styled(MuiDivider)(spacing)

function CompanyDetails() {
    const { t } = useTranslation()
    const { language } = useAppLocale()
    const { user } = useAuth()
    const company: CompanyEntity = useLoaderData()

    const noSharesDelisted = company?.shares.some((share) => share.delistedDate === null)

    // most recent delisting date, or null if there is at least one share that is not delisted
    const delistedDate = noSharesDelisted
        ? null
        : company?.shares.reduce(
              (acc, share) => {
                  if (share.delistedDate === null) return acc
                  if (acc === null || share.delistedDate > acc) {
                      acc = share.delistedDate
                  }
                  return acc
              },
              null as string | null,
          )

    return (
        <React.Fragment>
            <Helmet title={t('site.companies.detailsTitle', { name: normalizedName(company?.name) })} />
            <span style={{ fontStyle: delistedDate ? 'italic' : 'normal' }}>
                {company?.countryCode && (
                    <Tooltip title={company.countryCode}>
                        <img src={flagPath(company.countryCode)} width={25} style={{ marginRight: '16px' }} />
                    </Tooltip>
                )}

                <Typography
                    variant='h3'
                    gutterBottom
                    style={{}}
                    sx={{
                        display: 'inline',
                    }}
                >
                    {normalizedName(company?.name)}
                    {delistedDate ? (
                        <span> {t('pages.companies.delistedSince', { date: formatDateYYYYMMDD(delistedDate, { locale: language }) })}</span>
                    ) : null}

                    <WatchListButton entityId={company.id} isInWatchList={!!company.isInWatchList} type='Company' />
                </Typography>
                <div>
                    {user?.groups?.includes('superadmin') && (
                        <Button variant='text' to={`/admin/pr-company?companyId=${company.id}`} LinkComponent={Link}>
                            {t('site.admin.viewAdminPr')}
                        </Button>
                    )}
                </div>
            </span>
            <Divider my={6} />
            <Grid container spacing={6}>
                <Grid size={12}>
                    {!company && <LinearProgress />}
                    {company && <CompanyDetailsView />}
                </Grid>
            </Grid>
        </React.Fragment>
    )
}

export default CompanyDetails
